import { Product, ShoppingCard } from '@package/core/src/shoppingCart/types';

import ApiClient from '../../../ApiClient';
import { PageIds } from '../../types';
import config from 'config-base';
import { AlgonomyAnalyticService } from './service';
import { Purchase } from './types';

class AlgonomyAnalytic implements AlgonomyAnalyticService {
  constructor(protected apiClient: ApiClient) {}

  // when a user completes an order this method sends data to algonomy
  public async recsForPlacementsPurchase(purchaseOrders: Array<Purchase>): Promise<void> {
    try {
      const purchases = purchaseOrders.map((purchase: Purchase) => {
        const urlParams = new URLSearchParams({
          brandCode: config.brandCode,
          placements: PageIds.PurchaseComplete,
          productId: purchase.productId,
          o: purchase.orderId.toString(),
          ppc: purchase.productPricesCent,
          q: purchase.quantity,
        });
        return urlParams;
      });
      await Promise.all(purchases.map((purchase) => this.apiClient.get(`/recsForPlacements?${purchase}`)));
    } catch (error) {
      console.log(error);
    }
  }

  // method for sending statistic to algonomy
  public async recsForPlacements(cartData: ShoppingCard, pageId = PageIds.ShoppingCart): Promise<void> {
    try {
      const subOrdersIds = this.getProdutIds(cartData);
      const urlParams = new URLSearchParams({
        brandCode: config.brandCode,
        placements: pageId,
        productId: subOrdersIds,
        log: 'true',
      });
      if (subOrdersIds === '') {
        urlParams.delete('productIds');
      }
      const res = await this.apiClient.get(`/recsForPlacements?${urlParams}`);
    } catch (error) {}
  }

  // method for sending statistic to algonomy when a user adds a product to the cart
  public async recsForPlacementsAdd(product: string): Promise<void> {
    try {
      const urlParams = new URLSearchParams({
        brandCode: config.brandCode,
        placements: PageIds.AddProduct,
        atcid: product,
      });
      const res = await this.apiClient.get(`/recsForPlacements?${urlParams}`);
    } catch (error) {
      console.log(error);
    }
  }

  // when a user search a specific item this method sends data to algonomy
  public async recsForPlacementsSearch(searchTerm = ''): Promise<any> {
    try {
      const urlParams = new URLSearchParams({
        brandCode: config.brandCode,
        placements: PageIds.Search,
        searchTerm,
      });
      const res = await this.apiClient.get(`/recsForPlacements?${urlParams}`);
      const json = res.json();
    } catch (error) {
      console.error(`can't set search results`, error);
    }
  }

  public async sendCategoryTerm(term: string) {
    try {
      const urlParams = new URLSearchParams({
        brandCode: config.brandCode,
        query: term,
      });
      await this.apiClient.get(`/search/track?${urlParams}`);
    } catch (error) {
      console.log('error while sending category data', error);
    }
  }

  public async sendBrandsPage() {
    try {
      const urlParams = new URLSearchParams({
        brandCode: config.brandCode,
        placements: PageIds.IndividualBrand,
      });
      await this.apiClient.get(`/recsForPlacements?${urlParams}`);
    } catch (error) {
      console.log('error while sending brands page data', error);
    }
  }

  public async sendCategoryPage(categoryId: string) {
    try {
      const urlParams = new URLSearchParams({
        brandCode: config.brandCode,
        placements: PageIds.Category,
        categoryId: categoryId,
      });
      await this.apiClient.get(`/recsForPlacements?${urlParams}`);
    } catch (error) {
      console.log('error while sending category data', error);
    }
  }

  public async sendError404() {
    try {
      const urlParams = new URLSearchParams({
        brandCode: config.brandCode,
        placements: PageIds.Error404,
      });
      await this.apiClient.get(`/recsForPlacements?${urlParams}`);
    } catch (error) {
      console.log('error while opening wrong page', error);
    }
  }

  // gets products ids from multiple suborders in order to send them to algonomy on submit order page
  private getProdutIds(cartData: ShoppingCard): string {
    const subOrders = (cartData.subOrders.map((order) => order.products).flat() || []) as Array<Product>;
    return subOrders.map((product: Product) => product?.id || '').join('|');
  }
}

export default AlgonomyAnalytic;
